import { DeleteOutlined, EditOutlined, EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Pagination, LocationTableRow } from '@app/models/locations/location-response.model';

import { getLocationTableData } from '@app/api/locations.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button, Popconfirm, Input, InputRef, Col } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export const LocationTable: React.FC = () => {
  const [tableData, setTableData] = useState<{ data: LocationTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<Record<string, { label: string; value: string }[]> | null>(null);
  const [form] = BaseButtonsForm.useForm();
  const lastFetchedRef = useRef(false);

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getLocationTableData({ ...pagination, ...filters })
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
            setStatusOptions(res.status_options);
          }
        })
        .catch((error: any) => {
          notificationController.error({
            message: error.message,
          });
        });
    },
    [isMounted, filters],
  );

  useEffect(() => {
    if (!lastFetchedRef.current) {
      lastFetchedRef.current = true;
      fetch(initialPagination);
    }
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      ...filters,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-location`, { state: { id: id } });
  };

  const handleEditRow = (id: number) => {
    navigate(`/edit-location`, { state: { id: id } });
  };

  const handleDeleteRow = (id: number) => {
    console.log(`Deleting row with id ${id}`);
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Locations`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'status',
      type: 'dropdown',
      options: statusOptions || { statusOptions: [] },
    },
  ];

  const columns: ColumnsType<LocationTableRow> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: '30%',
    },
    {
      title: 'Location Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '30%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            <Tooltip title="Edit">
              <Button
                type="text"
                onClick={() => handleEditRow(record.id)}
                icon={<EditOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>

            <Tooltip title="Delete">
              <Popconfirm
                title="Are you sure to delete this role?"
                onConfirm={() => handleDeleteRow(record.id)}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              >
                <Button type="text" danger icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  return (
    <BaseCard id="basic-table" title="Locations" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
